import {
  AutopilotTaskDto,
  useAssignTaskToUser,
  useInvalidateUseGetAutopilotTasksForCampaign,
} from '@urbanx/agx.activities.client'
import {
  AgxRow,
  AgxColumn,
  AgxIconTask,
  AgxTypography,
  IconTaskTypes,
  AgxProgressBar,
  AgxEmptyState,
  Icon_Clock,
  AgxButton,
  Icon_CheckmarkCircleOutline,
  Icon_Chevron,
  AgxNotificationBox,
} from '@urbanx/agx.ui-components2'
import Panel from 'components/panel/Panel'
import TaskAssigneeSelect from 'components/tasks/TaskAssigneeSelect'
import { splitPascalCase } from 'utils/stringUtils'
import { useSelectedCampaign } from 'hooks'
import { JSX, useState, useContext } from 'react'
import { AuthContext } from '@urbanx/agx.auth.client'
import { useGetAssigneeOptions } from 'pages/allTasks/TaskFilters/useGetAssigneeOptions'
import AgreementComplianceModal from 'pages/allTasks/ComplianceModal/AgreementComplianceModal'
import { getStepCompletionPercentage } from 'utils/tasks'

interface AgreementComplianceProps {
  tasks: AutopilotTaskDto[]
  badge: JSX.Element
}

const AgreementCompliance = ({ tasks, badge }: AgreementComplianceProps) => {
  const { userAccount } = useContext(AuthContext)
  const allSteps = tasks.flatMap((t) => t.taskSteps ?? [])

  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign()
  const { assigneeOptions } = useGetAssigneeOptions()

  const { campaign } = useSelectedCampaign()

  const campaignId = campaign?.campaignId
  const { assignTaskToUser } = useAssignTaskToUser({
    onSuccess: () => {
      invalidateTasks(campaign?.campaignId ?? '')
    },
    onError: () => {},
  })
  const [isAgreementComplianceModalOpen, setIsAgreementComplianceModalOpen] =
    useState(false)

  const badgeAssingee = (
    <AgxRow alignItems="alignCenter" gap="2xs">
      <TaskAssigneeSelect
        id={'assignedto'}
        value={tasks[0]?.assignedTo}
        options={assigneeOptions}
        onAssign={(userId) => {
          if (!campaignId) return

          tasks.forEach((t) => {
            if (!t.category?.taskType) return

            assignTaskToUser({
              userId,
              campaignId,
              category: t.category,
            })
          })
        }}
      />
      {badge}
    </AgxRow>
  )

  return (
    <Panel key={'AgreementCompliancePanel'} padding={'spacing-4xl'}>
      <AgxRow fill>
        {campaign?.campaignId && (
          <AgreementComplianceModal
            tasks={tasks}
            isOpen={isAgreementComplianceModalOpen}
            onClose={() => setIsAgreementComplianceModalOpen(false)}
            campaignId={campaign?.campaignId ?? ''}
          />
        )}
        <AgxColumn fill gap="spacing-2xl">
          <AgxRow fill alignItems="alignCenter" justifyContent="space-between">
            <AgxRow gap="spacing-lg" alignItems="alignCenter">
              <AgxIconTask
                type={IconTaskTypes.AgreementCompliance}
                iconColor="black"
                height={32}
                width={32}
                bgColor="var(--bg-progress)"
              />
              <AgxTypography id={'title'} type={'heading'} size={'medium'}>
                Agreement Compliance
              </AgxTypography>
            </AgxRow>
            {badgeAssingee}
          </AgxRow>
          {tasks.every((t) => t.isCompleted === true) ? (
            <AgxTypography
              id={`agreement-compliance-completed`}
              type={'caption'}
              size={'medium'}
              color="var(--text-success)"
            >
              Complete! 🎉
            </AgxTypography>
          ) : (
            <>
              <AgxProgressBar
                variant="slim"
                id="progress"
                percentageProgressPosition="left"
                mode="dark"
                progress={getStepCompletionPercentage(allSteps ?? [])}
              />
              {tasks.every((t) => !!t.assignedTo) ? (
                <AgxColumn fill gap="spacing-3xl">
                  <div className="taskStep-checkboxContainer">
                    <AgxColumn fill gap="spacing-2xl">
                      {allSteps.map((step) => {
                        const name = splitPascalCase(step.name) ?? 'Untitled'
                        const key = `${step.order ?? 'no-order'}-${name}`

                        return (
                          <AgxRow
                            key={key}
                            gap="spacing-md"
                            alignItems="flex-end"
                            fill
                            justifyContent="left"
                          >
                            {step.isCompleted ? (
                              <Icon_CheckmarkCircleOutline color="var(--fg-success)" />
                            ) : (
                              <Icon_Clock color="var(--text-tertiary)" />
                            )}

                            <AgxTypography
                              id="steps"
                              size="small"
                              type="body_ui"
                              color="var(--text-tertiary)"
                              key={key + 'title'}
                            >
                              {name}
                            </AgxTypography>
                          </AgxRow>
                        )
                      })}
                      {campaign?.flatCommissionBelowMinimumAlert && (
                        <AgxNotificationBox
                          type="warning"
                          heading=""
                          body="Commission rate is low. Alert Admin Manager and confirm rate is correct before proceeding."
                        />
                      )}
                    </AgxColumn>
                  </div>

                  <AgxRow
                    id="completeTaskRow"
                    justifyContent="center"
                    fill
                    alignItems="alignCenter"
                  >
                    <AgxButton
                      id="completeTaskBtn"
                      variant="primary"
                      size="small"
                      text="Complete Compliance Check"
                      rightIcon={
                        <Icon_Chevron
                          width={18}
                          height={18}
                          direction={'right'}
                          color="var(--fg-brand-main)"
                        />
                      }
                      disabled={
                        tasks[0]?.assignedTo === null ||
                        tasks[0]?.assignedTo !== userAccount.agent
                      }
                      onClick={() => setIsAgreementComplianceModalOpen(true)}
                    />
                  </AgxRow>
                </AgxColumn>
              ) : (
                <AgxRow fill alignItems="alignCenter" justifyContent="center">
                  <AgxEmptyState
                    variant="advanced"
                    glyphType="locked"
                    actionButtons={[]}
                    label="Assign task to unlock"
                  />
                </AgxRow>
              )}
            </>
          )}
        </AgxColumn>
      </AgxRow>
    </Panel>
  )
}
export default AgreementCompliance
